<template>
  <v-row dense>
    <v-col v-if="props.quoteSave" cols="12">
      <autocomplete-field
        v-model="quoteData.status"
        data-testid="input-status"
        :prepend-inner-icon="mdiListStatus"
        label="Status"
        :disabled="props.disabled"
        :items="STATUSES"
        :success="statusValidation.success"
        :error-messages="statusValidation.errorMessages"
        @update:model-value="updateStatus"
      >
        <template v-if="props.quoteSave" #append-inner>
          <active-save-indicator
            :controller="savingBuffer.status.controller.value"
          />
        </template>
      </autocomplete-field>
    </v-col>

    <v-col
      cols="12"
      class="rounded"
      :class="{
        'error-sheet-outlined': productTypeValidation.errorMessages.length
      }"
    >
      <v-expansion-panels v-model="expandedTypes" multiple>
        <quote-request-product-type
          v-for="(type, index) in types"
          :key="index"
          v-bind="type.props"
          v-on="type.listeners"
        />
      </v-expansion-panels>

      <div
        v-if="productTypeValidation.errorMessages.length"
        class="v-messages text-error opacity-100 mt-3"
      >
        <div class="v-messages__wrapper">
          <div class="v-messages__message mx-3">
            {{ productTypeValidation.errorMessages[0] }}
          </div>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script setup>
import QuoteRequestProductType from "@/components/quotes/quote-request/product-type/QuoteRequestProductType.vue";
import ActiveSaveIndicator from "@/components/shared/active-save/ActiveSaveIndicator.vue";

import { STATUSES, QuoteRequestGroupInfo } from "@/models/Quote";
import { useActiveSave } from "@/composables/active-save.composable";
import { ref, watch } from "vue";
import { computedValidation } from "@/util/helpers";
import useVuelidate from "@vuelidate/core";

import { mdiListStatus } from "@mdi/js";
import {
  addEnabled,
  baseTypeGenerator,
  basicTypeEnabled
} from "@/components/quotes/quote-request/product-type/product-type-util.mjs";
import { computed } from "vue";

const GROUP_OPTION = {
  LIFE: "Life",
  DISABILITY: "Disability",
  VISION: "Vision",
  DENTAL: "Dental",
  LTC: "LTC"
};

const props = defineProps({
  disabled: Boolean,

  status: { type: String, default: "Pending" },
  productType: { type: Array, required: true },
  quoteSave: {
    type: Function,
    required: false,
    default: null
  }
});

const quoteData = ref(QuoteRequestGroupInfo(props));
const expandedTypes = ref([]);

const savingBuffer = {};
Object.keys(QuoteRequestGroupInfo()).forEach(key => {
  savingBuffer[key] = useActiveSave();
});

const emit = defineEmits([
  ...Object.keys(QuoteRequestGroupInfo()).map(key => `update:${key}`),
  "save"
]);

const v$ = useVuelidate(
  {
    quoteData: {
      status: { required: v => !props.quoteSave || !!v },
      productType: {
        required: v => Boolean(v?.length),
        validCombination: v => {
          if (
            [GROUP_OPTION.VISION, GROUP_OPTION.DENTAL].some(type =>
              v.includes(type)
            )
          ) {
            return [GROUP_OPTION.LIFE, GROUP_OPTION.DISABILITY].some(type =>
              v.includes(type)
            );
          }

          return true;
        }
      }
    }
  },
  { quoteData },
  { $scope: "quote-request", $autoDirty: true }
);
const lifeEnabled = basicTypeEnabled(quoteData, GROUP_OPTION.LIFE);
const ltcEnabled = basicTypeEnabled(quoteData, GROUP_OPTION.LTC);
const disabilityEnabled = basicTypeEnabled(quoteData, GROUP_OPTION.DISABILITY);
const dentalEnabled = basicTypeEnabled(quoteData, GROUP_OPTION.DENTAL);
const visionEnabled = basicTypeEnabled(quoteData, GROUP_OPTION.VISION);
const enabledProps = {
  save: saveInjector("productType"),
  validator: v$.value.quoteData.productType.$validate
};

const lifeConfig = computed(() => {
  const config = baseTypeGenerator("Life", props.disabled);
  addEnabled(config, lifeEnabled, enabledProps);
  return config;
});

const ltcConfig = computed(() => {
  const config = baseTypeGenerator("Long Term Care", props.disabled);
  addEnabled(config, ltcEnabled, enabledProps);
  return config;
});

const disabilityConfig = computed(() => {
  const config = baseTypeGenerator("Disability", props.disabled);
  addEnabled(config, disabilityEnabled, enabledProps);
  return config;
});

const dentalConfig = computed(() => {
  const config = baseTypeGenerator("Dental", props.disabled);
  addEnabled(config, dentalEnabled, enabledProps);
  return config;
});

const visionConfig = computed(() => {
  const config = baseTypeGenerator("Vision", props.disabled);
  addEnabled(config, visionEnabled, enabledProps);
  return config;
});

const types = computed(() => {
  return [
    dentalConfig.value,
    disabilityConfig.value,
    lifeConfig.value,
    ltcConfig.value,
    visionConfig.value
  ];
});

const statusValidation = computedValidation(v$.value.quoteData.status, {
  required: "Required"
});

const productTypeValidation = computedValidation(
  v$.value.quoteData.productType,
  {
    required: "Required",
    validCombination: "Vision and/or Dental require Life and/or Disability"
  }
);

function updateStatus() {
  return savingBuffer.status.update(() => props.quoteSave("status"));
}

function saveInjector(key) {
  if (props.quoteSave) return () => props.quoteSave(key);
  return null;
}

Object.keys(QuoteRequestGroupInfo()).forEach(key => {
  watch(
    () => quoteData.value[key],
    v => {
      emit(`update:${key}`, v);
    },
    { deep: true }
  );
});
</script>
